import React, {useState} from 'react';
import {View, ImageBackground, ScrollView} from 'react-native';
import GestureRecognizer from 'react-native-swipe-gestures';

import Text from '../../elements/Text';

import makeStyles from './styles';
import {useTheme, useThemedStyles} from '../../../theme';
import {BS_CARD_TYPES} from '../../../utils/Constants';
import {Images} from '../../../utils/Images';
import {DLog} from '../../../utils/HelperMethods';
import {useTimeFormat, formatTime} from '../../../hooks/useTimeFormat';
import Dots from 'react-native-dots-pagination';
import {
  HyperArrowIcon,
  HypoArrowIcon,
  NormalLevelIcon,
  RecentReadingsIcon,
} from '../../../utils/SvgIcons';
import CommonStyles from '../../../utils/CommonStyles';

// variations supported
// 1. Big Card
// 2. Small Card
// 3. Swipable Card
const BloodSugarCard = ({
  variation = BS_CARD_TYPES.BigCard,
  value,
  min,
  max,
  title = 'Period Avg',
  readings = {data: []},
}) => {
  const {colors} = useTheme();
  const styles = useThemedStyles(makeStyles);
  const [active, setActive] = useState(0);
  const timeFormat = useTimeFormat();
  const cardsRequired = Math.ceil(readings?.data?.length / 4);
  // DLog('props as', readings?.data);
  const BigCard = () => {
    return (
      <>
        <Text variation="Body1Regular" color={colors.textHeading}>
          {title}
        </Text>
        <ImageBackground
          resizeMode="center"
          source={
            value > max
              ? Images.ringOrange
              : value < min
              ? Images.ringBlue
              : Images.ringGreen
          }
          style={styles.circleContainer}>
          <Text color={colors.textHeading} variation="HeadlineBold">
            {value}
          </Text>
          <Text color={colors.textMuted} variation="Subtitle2Regular">
            mg/dL
          </Text>
        </ImageBackground>
      </>
    );
  };

  const SmallCard = () => {
    return (
      <>
        <View style={styles.smallCard}>
          <View style={styles.smallCardTitle}>
            <Text variation="Body1Regular" color={colors.textHeading}>
              {title}
            </Text>
            {value > max ? (
              <HyperArrowIcon />
            ) : value < min ? (
              <HypoArrowIcon />
            ) : (
              <NormalLevelIcon />
            )}
          </View>
          <View>
            <Text color={colors.textHeading} topMargin={12}>
              {value}{' '}
              <Text color={colors.textMuted} variation="Subtitle2Regular">
                mg/dL
              </Text>
            </Text>
          </View>
        </View>
      </>
    );
  };

  const renderLine = (
    item,
    index, //render function used by map
  ) => (
    <View
      style={[styles.smallCardTitle, CommonStyles.mt_8]}
      key={`${item.measureAt}-${index}`}>
      <Text variation="Body1Regular" color={colors.textMuted}>
        {formatTime(item.measureAt, timeFormat)}
      </Text>
      <Text color={colors.textPrimary}>
        {item.diabetesMeasurement.bloodSugarCount}
      </Text>
    </View>
  );

  const BigCard2 = () => {
    // Get readings in reverse order (most recent first)
    const reversedData = [...(readings?.data || [])].reverse();

    // Slice based on active page (4 items per page)
    const displayData = active
      ? reversedData.slice((active - 1) * 4, active * 4)
      : reversedData;

    return (
      <ScrollView showsVerticalScrollIndicator={false}>
        <View style={styles.smallCardTitle}>
          <Text variation="Body1Regular" color={colors.textHeading}>
            Recent
          </Text>
          <RecentReadingsIcon />
        </View>
        {displayData.map((item, index) => renderLine(item, index))}
      </ScrollView>
    );
  };

  const SwipableCard = () => {
    return (
      <>
        <GestureRecognizer
          onSwipeLeft={state =>
            active == cardsRequired ? null : setActive(active + 1)
          }
          onSwipeRight={state => (active == 0 ? null : setActive(active - 1))}
          style={CommonStyles.full_flex}>
          {active == 0 ? <BigCard /> : <BigCard2 />}
        </GestureRecognizer>
        <Dots
          length={1 + cardsRequired}
          active={active}
          paddingHorizontal={8}
          marginHorizontal={8}
          activeDotWidth={7}
          activeDotHeight={7}
          passiveDotHeight={7}
          passiveDotWidth={7}
          activeColor={colors.textMuted}
          passiveColor={colors.borderMuted}
        />
      </>
    );
  };

  return (
    <View style={styles.container(variation)}>
      {variation === BS_CARD_TYPES.BigCard && <BigCard />}
      {variation === BS_CARD_TYPES.SmallCard && <SmallCard />}
      {variation === BS_CARD_TYPES.SwipableCard && <SwipableCard />}
    </View>
  );
};

export default BloodSugarCard;
